import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { JmfPermissionEntity, PermissionNameType } from "@mm-products/java-multima-framework";
import Container from "@mui/material/Container";
import NavigationMobile from "./NavigationMobile";
import NavigationDesktop from "./NavigationDesktop";
import NavigationUserMenu from "./user/NavigationUserMenu";
import { TaxPermissionBuilder, TaxPermissionEntity } from "../permissions/TaxGuruPermissionBuilder";
import i18n from "../i18n";
import CreditComponent from "../modules/licence/component/CreditComponent";
import NotificationCenter from "./notification/NotificationCenter";
import Stack from "@mui/material/Stack";
import BatchTaskIndicator from "./batch/BatchTaskIndicator";
import { SUPER_ADMIN_ROLE } from "../permissions/usePermissions";

export type NavigationEntryDivider = {
    divider: true;
};
export type NavigationEntryItem = {
    divider?: false | undefined;
    title: string;
    link: string;
    permissionsAtLeastOne?: Array<PermissionNameType>;
};
export type NavigationEntry = NavigationEntryDivider | NavigationEntryItem;

export const passwordResetLink = "/reset-password";
export const registrationLink = "/registration";
export const navigationLinks = {
    // top navigation
    TAX_DOCUMENTS_LINK: "/documents",
    TAX_ACCOUNTANTS_LINK: "/accountants",
    TAX_CLIENTS_LINK: "/clients",
    TAX_LICENCES_LINK: "/licences",
    TAX_OFFICERS_LINK: "/officers",
    TAX_APPROVERS_LINK: "/approvers",
    // lists
    TAX_PREACCOUNTING_LINK: "/preaccounting",
    TAX_CENTRES_LINK: "/centres",
    TAX_COMPANIES_LINK: "/companies",
    TAX_CONTACTS_LINK: "/contacts",
    TAX_CONTRACT_LINK: "/contracts",
    TAX_ACTIVITY_LINK: "/activities",
    // admin
    ADMIN_USERS_LINK: "/admin/users",
    ADMIN_ROLES_LINK: "/admin/roles",
    ADMIN_SCHEDULERS_LINK: "/admin/schedulers",
    ADMIN_TASKS_LINK: "/admin/tasks",
    ADMIN_ATTACHMENTS_LINK: "/admin/attachments",
    ADMIN_PERSONS_LINK: "/admin/persons",
    ADMIN_ADDRESSES_LINK: "/admin/addresses",
    ADMIN_AUDIT: "/admin/audit",
    ADMIN_PAYMENT_TRANSACTIONS_LINK: "/admin/transactions",
    ADMIN_PAYMENT_REQUESTS_LINK: "/admin/payment-requests",
    ADMIN_PAYMENT_ORDERS_LINK: "/admin/payment-orders",
    ADMIN_PAYMENT_OFFERS_LINK: "/admin/payment-offers",
    ADMIN_PAYMENT_COUPONS_LINK: "/admin/payment-coupons",
    ADMIN_SUBSCRIPTIONS_LINK: "/admin/subscriptions",
    ADMIN_VAT_RATES_LINK: "/admin/vat-rates",
    ADMIN_FEEDBACKS_LINK: "/admin/feedbacks",
    ADMIN_ONBOARDING_FEEDBACKS_LINK: "/admin/onboarding-feedbacks",
    ADMIN_REGISTRATION_REQUESTS_LINK: "/admin/registration-requests",
    ADMIN_EMAILS_LINK: "/admin/emails",
    // about
    ABOUT_LINK: "/about",
};

export const dashboardPermission = TaxPermissionBuilder.builder().menu("TaxDocument_dashboard").build();
export const nonAdminItems: Array<NavigationEntry> = [
    {
        title: i18n.t("navigation.overview"),
        link: "/",
        permissionsAtLeastOne: dashboardPermission,
    },
    {
        title: i18n.t("navigation.documents"),
        link: navigationLinks.TAX_DOCUMENTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_DOCUMENT).build(),
    },
    {
        title: i18n.t("navigation.accountants"),
        link: navigationLinks.TAX_ACCOUNTANTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_ACCOUNTANT).build(),
    },
    {
        title: i18n.t("navigation.clients"),
        link: navigationLinks.TAX_CLIENTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_CLIENT).build(),
    },
    {
        title: i18n.t("navigation.licences"),
        link: navigationLinks.TAX_LICENCES_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_LICENCE).build(),
    },
];

export const navigationSettingItems: Array<NavigationEntry> = [
    {
        title: i18n.t("navigation.accounts"),
        link: navigationLinks.ADMIN_USERS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_USER).build(),
    },
    {
        title: i18n.t("navigation.role"),
        link: navigationLinks.ADMIN_ROLES_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_ROLE).build(),
    },

    { divider: true },
    {
        title: i18n.t("navigation.persons"),
        link: navigationLinks.ADMIN_PERSONS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_PERSON).build(),
    },
    {
        title: i18n.t("navigation.addresses"),
        link: navigationLinks.ADMIN_ADDRESSES_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_ADDRESS).build(),
    },
    {
        title: i18n.t("navigation.contacts"),
        link: navigationLinks.TAX_CONTACTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_CONTACT).build(),
    },
    {
        title: i18n.t("navigation.companies"),
        link: navigationLinks.TAX_COMPANIES_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_COMPANY).build(),
    },
    { divider: true },
    {
        title: i18n.t("navigation.registrationRequests"),
        link: navigationLinks.ADMIN_REGISTRATION_REQUESTS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    {
        title: i18n.t("navigation.emails"),
        link: navigationLinks.ADMIN_EMAILS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    { divider: true },
    {
        title: i18n.t("navigation.activities"),
        link: navigationLinks.TAX_ACTIVITY_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_ACTIVITY).build(),
    },
    {
        title: i18n.t("navigation.preAccounting"),
        link: navigationLinks.TAX_PREACCOUNTING_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_PREACCOUNTING).build(),
    },
    {
        title: i18n.t("navigation.centres"),
        link: navigationLinks.TAX_CENTRES_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_CENTRE).build(),
    },
    {
        title: i18n.t("navigation.contracts"),
        link: navigationLinks.TAX_CONTRACT_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_CONTRACT).build(),
    },
    { divider: true },
    {
        title: i18n.t("navigation.officers"),
        link: navigationLinks.TAX_OFFICERS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_OFFICER).build(),
    },
    {
        title: i18n.t("navigation.approvers"),
        link: navigationLinks.TAX_APPROVERS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(TaxPermissionEntity.TAX_APPROVER).build(),
    },
    { divider: true },
    {
        title: i18n.t("navigation.scheduler"),
        link: navigationLinks.ADMIN_SCHEDULERS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_SCHEDULER).build(),
    },
    {
        title: i18n.t("navigation.tasks"),
        link: navigationLinks.ADMIN_TASKS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_TASK_QUEUE).build(),
    },
    { divider: true },
    {
        title: i18n.t("navigation.dms"),
        link: navigationLinks.ADMIN_ATTACHMENTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().menu(JmfPermissionEntity.CORE_ATTACHMENT).build(),
    },
    { divider: true },
    {
        title: i18n.t("navigation.audit"),
        link: navigationLinks.ADMIN_AUDIT,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    { divider: true },
    {
        title: i18n.t("navigation.paymentRequests"),
        link: navigationLinks.ADMIN_PAYMENT_REQUESTS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_REQUEST).build(),
    },
    {
        title: i18n.t("navigation.paymentTransactions"),
        link: navigationLinks.ADMIN_PAYMENT_TRANSACTIONS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    {
        title: i18n.t("navigation.paymentOrders"),
        link: navigationLinks.ADMIN_PAYMENT_ORDERS_LINK,
        permissionsAtLeastOne: TaxPermissionBuilder.builder().read(JmfPermissionEntity.CORE_PAYMENT_ORDER).build(),
    },
    {
        title: i18n.t("navigation.paymentOffers"),
        link: navigationLinks.ADMIN_PAYMENT_OFFERS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    {
        title: i18n.t("navigation.paymentCoupons"),
        link: navigationLinks.ADMIN_PAYMENT_COUPONS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    {
        title: i18n.t("navigation.subscriptions"),
        link: navigationLinks.ADMIN_SUBSCRIPTIONS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    { divider: true },
    {
        title: i18n.t("navigation.vatRates"),
        link: navigationLinks.ADMIN_VAT_RATES_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    { divider: true },
    {
        title: i18n.t("navigation.feedbacks"),
        link: navigationLinks.ADMIN_FEEDBACKS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
    {
        title: i18n.t("navigation.onboardingFeedbacks"),
        link: navigationLinks.ADMIN_ONBOARDING_FEEDBACKS_LINK,
        permissionsAtLeastOne: [SUPER_ADMIN_ROLE],
    },
];

const Navigation = () => {
    return (
        <AppBar position="sticky">
            <Container maxWidth="xl" sx={{ overflowY: "auto" }}>
                <Toolbar disableGutters>
                    <Box sx={{ flexGrow: 1, display: { xs: "block", md: "block", lg: "none" } }}>
                        <NavigationMobile />
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: "none", lg: "block" } }}>
                        <NavigationDesktop />
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={{ xs: 2, md: 1, lg: 2 }}>
                        <Box sx={{ display: { xs: "none", lg: "block" } }}>
                            <CreditComponent />
                        </Box>
                        <BatchTaskIndicator />
                        <NotificationCenter />
                        <NavigationUserMenu />
                    </Stack>
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Navigation;
